import { generatePath } from 'react-router';

import { DRIVE_PAGE, TASKS_PAGE } from '@/common/constants/pages';
import { addPrefixToHost } from '@/common/helpers/addPrefixToHost';
import DebugModeService from '@/common/services/DebugModeService';
import { DRIVE_SEARCH_QUERY_PARAM } from '@/drive/constants/driveSearch';
import { DriveSection } from '@/drive/types/DriveSection';
import { GasolineMovieType } from '@/gasolineMovies/entity/GasolineMovie';
import SetupPreset from '@/media/entity/SetupPreset';
import { ProductType } from '@/product/constants/productTypes';
import { ProductId } from '@/product/entity/Product';
import { ProductEntity } from '@/product/types';
import { TasksFilterValues } from '@/tasks/types/tasksFilterValues';
import { User } from '@/user/entity/User';
import { WidgetTemplateName } from '@/widget/constants/WidgetTemplateName';

import URLBuilder from '../network/URLBuilder';

export const getURLPrefixForProductType = (productType: Nullable<ProductType>) => {
  switch (productType) {
    case ProductType.Jewelry:
      return 'jewelry';
    case ProductType.Rough:
    case ProductType.LGDRough:
      return 'rough';
    case ProductType.Gem:
      return 'gem';
    default:
      return 'diamond'; // ignore 'diamond-colorless' or 'diamond-fancy', just 'diamond'
  }
};

export default class URLProvider {
  static productEditPage(product: ProductEntity) {
    return `/${product.entityType}/edit/${product.id}`;
  }

  static dilogLink(productId: number) {
    const base = addPrefixToHost('dilog');

    return URLBuilder.build(base, `product/${productId}`, {});
  }

  static productDetailPage(
    product: Nullable<Pick<ProductEntity, 'id' | 'type'> & { isReference?: boolean }> = null,
    setupPreset: Nullable<SetupPreset> = null,
    gasolineMovieType: Nullable<GasolineMovieType> = null,
  ) {
    if (!product || product.isReference) {
      return '';
    }

    if (product.id && product.type) {
      const urlPrefix = getURLPrefixForProductType(product.type);

      const nextParams: Record<string, string | number> = {};

      if (setupPreset?.id) {
        nextParams['sp'] = setupPreset.id;
      }

      if (gasolineMovieType) {
        nextParams['movie'] = gasolineMovieType;
      }

      if (DebugModeService.isEnabled) {
        nextParams['debug'] = '1';
      }

      if (URLBuilder.params().meta === '1') {
        nextParams['meta'] = '1';
      }

      return `/${urlPrefix}/${product.id}${URLBuilder.buildQueryString(nextParams)}`;
    }

    return '';
  }

  static comparisonPage(ids: number[], productType: ProductType) {
    const productsURIPart = ids ? ids.map((id) => `id[]=${id}`).join('&') : '';

    return `/compare/${productType}${productsURIPart ? `?${productsURIPart}` : ''}`;
  }

  static referenceComparisonPage(productId: number, productType: ProductType) {
    return `/compare/reference/${productType}/${productId}`;
  }

  static solutionsPage(projectId: string, query?: Record<string, unknown>) {
    return `/projects/${projectId}/solutions${query ? URLBuilder.buildQueryString(query) : ''}`;
  }

  static activeSolutionPage(solutionId: StringOrNumber, projectId: string, projectCBR?: Nullable<string>) {
    if (!projectCBR) {
      return URLProvider.solutionsPage(projectId);
    }

    return URLProvider.solutionPage(solutionId, projectId);
  }

  static solutionPage(solutionId: StringOrNumber, projectId: string) {
    return `/projects/${projectId}/solutions?activeSolutionId=${solutionId}`;
  }

  static projectsPage() {
    return '/projects';
  }

  static tasksPage(filter: Partial<TasksFilterValues> = {}) {
    return `${TASKS_PAGE}${URLBuilder.buildQueryString(filter)}`;
  }

  static sharedWithMeProjectsPage() {
    return '/shared-with-me-projects';
  }

  static profile(user: User) {
    if (user.isAnonymous) {
      return '';
    }

    return '/profile';
  }

  static collectionDetailPage(collection: { id: string }) {
    return `/~${collection.id}`;
  }

  static collectionEditPage(collection: { id: string }) {
    return `/~${collection.id}/edit`;
  }

  static customGradingCollectionPage(collectionId: string, customGradeCode: string) {
    return `/grading/custom/${customGradeCode}?c=${collectionId}`;
  }

  static customGradingDiamondPage(diamondId: number, customGradeCode: string) {
    return `/grading/custom/${customGradeCode}?id[]=${diamondId}`;
  }

  static backOfficePage(productType: ProductType) {
    return `/catalog/${productType}/edit`;
  }

  static productQcPage(productId1: number, productId2: number) {
    return URLBuilder.buildUrl('/api/internal/qc', { url1: productId1, url2: productId2 });
  }

  static adminPage() {
    return URLBuilder.buildUrl('/', {}, 'admin');
  }

  static adminCopyExportPage(kind: 'product' | 'project', id: StringOrNumber) {
    return URLBuilder.buildUrl(`/transfers/export/${kind}/${id}`, {}, 'admin');
  }

  static widgetUrl(templateName: WidgetTemplateName, productId: number, params: Record<string, unknown> = {}) {
    if (templateName === WidgetTemplateName.Basic) {
      return URLBuilder.buildUrl(`${templateName}/${productId}`, params, 'widget-v3');
    }

    if (templateName === WidgetTemplateName.Story) {
      return URLBuilder.buildUrl(`${templateName}/${productId}`, params, 'widget-v3');
    }

    if (templateName === WidgetTemplateName.RapNet || templateName === WidgetTemplateName.Video) {
      return URLBuilder.buildUrl(`${templateName}/${productId}`, params, 'widget');
    }

    return URLBuilder.buildUrl(`${templateName}/v1/${productId}`, params, 'widget');
  }

  static convertRelativeToAbsolute(url: string) {
    return `${window.location.origin}${url}`;
  }

  static convertToRelativeLink(absoluteLink: string) {
    return new URL(absoluteLink).pathname;
  }

  static driveSpectrumPage() {
    return '/drive/spectrum';
  }

  static driveSearchPage(query: string) {
    const path = generatePath(DRIVE_PAGE, { section: DriveSection.Search });

    return `${path}${URLBuilder.buildQueryString({ [DRIVE_SEARCH_QUERY_PARAM]: query })}`;
  }

  static roughSpectrumPath(productId: ProductId) {
    return `/clarityplotting/${productId}/spectrum`;
  }

  static clarityPlottingDemoPath(productId: ProductId) {
    return `/clarityplotting/${productId}/demo`;
  }
}
